import Image from 'next/image'
import { ComponentPropsWithoutRef } from 'react'
import { cn } from '~shared/utils/cn'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@/modules/components/ui/carousel'
import { ArrowUpIcon } from '~shared/icons/arrow-up'
import { getTeachersData } from '@/modules/program-page'

interface TeacherCardProps extends ComponentPropsWithoutRef<'div'> {
  name: string
  label: string
  description: string
  image: {
    url: string
    alternativeText: string
    width: number
    height: number
  }
}
const TeacherCard = (props: TeacherCardProps) => {
  const { className, name, label, description, image, ...rest } = props

  return (
    <div
      className={cn(
        'relative rounded-[24px] overflow-hidden group cursor-pointer',
        className
      )}
      {...rest}>
      <div className='absolute inset-0'>
        <Image
          alt={image.alternativeText || 'teacher'}
          src={image.url}
          width={image.width}
          height={image.height}
          className='size-full object-cover group-hover:opacity-0 transition-opacity duration-300'
          draggable={false}
        />
      </div>
      <div
        className='inset-0 absolute group-hover:opacity-0'
        style={{
          background:
            'linear-gradient(to top, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 60%, rgba(0, 0, 0, 0) 100%)'
        }}
      />

      <div className='size-full flex flex-col justify-between relative p-[24px]'>
        <button className='rounded-full flex items-center justify-center size-[50px] bg-primary self-end'>
          <ArrowUpIcon className='rotate-45 size-[24px]' />
        </button>
        <div className='text-primary'>
          <p className='font-medium leading-[1] tracking-[-0.01em] text-[32px]'>
            {name}
          </p>
          <p className='mt-[16px] leading-[1.1] text-[16px]'>{label}</p>
        </div>
      </div>
      <div
        className={cn(
          'absolute inset-0 bg-secondary rounded-lg shadow-md overflow-hidden p-[24px] flex flex-col justify-between',
          'opacity-0 group-hover:opacity-100 transition-opacity duration-300'
        )}>
        <div className='flex justify-between'>
          <Image
            alt={image.alternativeText || 'teacher'}
            src={image.url}
            width={image.width}
            height={image.height}
            className='size-[100px] rounded-full object-cover'
            draggable={false}
          />
          <button className='self-start rounded-full flex items-center justify-center size-[50px] bg-primary'>
            ...
          </button>
        </div>
        <div>
          <p className='font-medium leading-[1] tracking-[-0.01em] text-[32px]'>
            {name}
          </p>
          <p
            className='mt-[16px] leading-[1.1] text-[16px]'
            dangerouslySetInnerHTML={{ __html: description }}
          />
        </div>
      </div>
    </div>
  )
}

export interface TeachersSectionProps
  extends ComponentPropsWithoutRef<'section'> {
  teachers: Awaited<Promise<ReturnType<typeof getTeachersData>>>
}

export const TeachersSection = (props: TeachersSectionProps) => {
  const { className, teachers, ...rest } = props

  return (
    <section className={cn('container container-slim', className)} {...rest}>
      <h2 className='typo-section-heading text-center'>Преподаватели</h2>
      <p className='typo-section-description mt-6 text-center'>
        Преподают ведущие российские и&nbsp;зарубежные эксперты с&nbsp;опытом
        от&nbsp;7&nbsp;до&nbsp;25&nbsp;лет
      </p>
      <Carousel
        className={cn('mt-[16px] lg:mt-[60px]', className)}
        opts={{
          align: 'start'
        }}
        aria-label='Журнал'
        {...rest}>
        <CarouselContent>
          {/* eslint-disable-next-line */}
          {/* @ts-ignore */}
          {teachers.map((teacher, index) => (
            <CarouselItem
              key={index}
              className='box-border basis-1/3 h-[500px]'>
              <TeacherCard
                className='w-full h-full'
                name={teacher.name || ''}
                description={teacher.description || ''}
                image={{
                  url: teacher.image?.url || '',
                  alternativeText: teacher.image?.alternativeText || '',
                  width: teacher.image?.width || 0,
                  height: teacher.image?.height || 0
                }}
                label='более 1500 проведённых консультаций'
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className='flex gap-4 mx-auto w-fit mt-[30px] md:mt-[40px] xl:mt-[60px]'>
          <CarouselPrevious size='lg' />
          <CarouselNext size='lg' />
        </div>
      </Carousel>
    </section>
  )
}
